import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import ChartColumnStacked from '../../../../components/charts/ChartColumnStacked'
import ChartColumnSkeleton from '../../../../components/skeletons/ChartColumnSkeleton'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import { DailyMonitoringFiltersContext } from '../../../../context/DailyMonitoringFiltersContext'
import { FilterDailyMonitoring } from '../../../../types/FilterDailyMonitoring'
import { formatDateCard } from '../../../../utils/Helpers'
import { STATUS_DELIVERED, STATUS_DELIVERING, STATUS_CONFIRMED, STATUS_WAITING_CONFIRMATION } from '../../../../types/Order'
import { defaultDateRange, defaultFormatTimeZone } from '../../../../utils/consts'

interface Bucket {
  key_as_string: string
  doc_count: number,
  total: {
    value: number
  }
}

const PharmaciesTopSales = (): JSX.Element => {
  const [dateInterval, setDateInterval] = useState('1d')
  const [calendarInterval, setCalendarInterval] = useState('calendar_interval')
  const GET_DATA = gql`
  query OrdersHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      query: {
        bool: {
          filter: [
            {
              terms: {
                status_translate_keyword: [
                  "${STATUS_DELIVERED}",
                  "${STATUS_DELIVERING}",
                  "${STATUS_CONFIRMED}",
                  "${STATUS_WAITING_CONFIRMATION}"
                ]
              }
            }
          ]
        }
      },
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            ${calendarInterval}: "${dateInterval}",
            time_zone: "-03:00"
          },
          aggs: {
            delivered: {
              filter: {
                term: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              },
              aggs: {
                total: {
                  sum: {
                    field: "total"
                  }
                }
              }
            },
            openedGmv: {
              filter: {
                terms: {
                  status_translate_keyword: [
                    "${STATUS_DELIVERING}",
                    "${STATUS_CONFIRMED}",
                    "${STATUS_WAITING_CONFIRMATION}"
                  ]
                }
              },
              aggs: {
                total: {
                  sum: {
                    field: "total"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

  const { filters } = useContext(DailyMonitoringFiltersContext)
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    let createdAt: FilterDailyMonitoring['dateRange'] = { ...defaultDateRange }

    if (filters.dateRange) {
      createdAt = {
        ...filters.dateRange,
        ...defaultFormatTimeZone
      }
    }

    const a = moment(createdAt.initialDate)
    const b = moment(createdAt.finalDate)
    const difference = b.diff(a, 'day')

    if (difference >= 0 && difference <= 15) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1d')
    } else if (difference >= 16 && difference <= 91) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1w')
    } else if (difference >= 92 && difference <= 182) {
      setCalendarInterval('fixed_interval')
      setDateInterval('15d')
    } else if (difference >= 183 && difference <= 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1M')
    } else if (difference > 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1q')
    }

    getData({
      variables: { filters: { ...filters, createdAt } }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <ChartColumnSkeleton />
  if (loading || !data) return <ChartColumnSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.ordersAggs.aggregations) return <p>No data found</p>

  const { buckets } = data.ordersAggs.aggregations.salesOverTime
  const categories: Array<string> = []
  const seriesData: Array<number> = []
  buckets.map((bucket: Bucket) => {
    categories.push(bucket.key_as_string)
    return seriesData.push(bucket.doc_count)
  })
  const arrayDelivered: any = buckets.map((bucket: any) => {
    return bucket.delivered.doc_count
  })
  const arrayOpenedGmv: any = buckets.map((bucket: any) => {
    return bucket.openedGmv.doc_count
  })

  const options = {
    chart: {
      id: 'finished-orders-history'
    },
    xaxis: {
      categories,
      type: 'datetime'
    },
    grid: {
      show: false
    }
  }
  const series = [{
    name: 'Pedidos em aberto',
    data: arrayOpenedGmv
  }, {
    name: 'Pedidos entregues',
    data: arrayDelivered
  }]

  if (!data) return <></>

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>
      Quantidade de pedidos entregues ({formatDateCard(filters.dateRange?.initialDate, filters.dateRange?.finalDate)})
      {dateInterval === '1d'
        ? (<Typography variant='caption'> filtrado por dia</Typography>)
        : dateInterval === '1w'
          ? (<Typography variant='caption'> filtrado por semana</Typography>)
          : dateInterval === '15d'
            ? (<Typography variant='caption'> filtrado por quinzena</Typography>)
            : dateInterval === '1M'
              ? (<Typography variant='caption'> filtrado por mês</Typography>)
              : dateInterval === '1q'
                ? (<Typography variant='caption'> filtrado por trimestre</Typography>)
                : ''
      }
    </Typography>
    <ChartColumnStacked options={options} series={series} theme={ChartLineThemes.twocolors} height={280} />
  </Paper>
}

export default PharmaciesTopSales
